import React, { useRef, useEffect, useState } from 'react';
import './WaveformViewer.css';
import { generateWaveformData } from '../utils/waveformGenerator';
import { logger } from '../utils/logger';

/**
 * Canvas palette - UNCONDITIONAL (TEM-1195).
 *
 * This canvas used to read `document.documentElement.getAttribute('data-theme')`
 * and paint the near-white #f0f0f2 when it saw "light". It was the ONLY thing in
 * Radio that actually responded to the sun/moon toggle, and it responded by
 * dropping a near-white slab into a near-black app: measured 17.49:1 against
 * `.waveform-viewer`'s `--ground` #08090d (packages/design/scripts/wcag.mjs).
 * Every CSS `[data-theme="light"]` rule had already been repointed at the dark
 * cinematic tokens by TEM-1131 (commit 9a1f94bb), so this canvas was the whole of
 * the reachable mixed state.
 *
 * Radio is a single committed dark world; there is no theme attribute to read.
 * Re-introducing a light variant means adopting the ecosystem's `cinematic-light`
 * layer (see the header comment in src/cinematic.css), not this branch.
 */
const CANVAS_BACKGROUND = '#1a1a1a';
const CANVAS_CENTER_LINE = '#333333';

// Quick mobile check to avoid expensive operations
const isMobile = (): boolean => {
  if (typeof window === 'undefined') return false;
  const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const touchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  return mobileUA || (touchDevice && window.innerWidth < 1024);
};

interface WaveformViewerProps {
  audioUrl: string;
  currentTime: number;
  duration: number;
  onSeek: (time: number) => void;
  height?: number;
  waveformData?: number[];
}

const WaveformViewer: React.FC<WaveformViewerProps> = ({
  audioUrl,
  currentTime,
  duration,
  onSeek,
  height = 80,
  waveformData,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [localWaveformData, setLocalWaveformData] = useState<number[]>([]);
  const [isHovering, setIsHovering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Generate waveform data if not provided
  useEffect(() => {
    if (waveformData) {
      setLocalWaveformData(waveformData);
      setIsLoading(false);
    } else if (audioUrl) {
      // On mobile, use a simple placeholder instead of generating
      // This prevents memory crashes from loading large audio files
      if (isMobile()) {
        logger.info('[WaveformViewer] Skipping waveform generation on mobile');
        // Simple sine wave placeholder
        const placeholder = Array(100).fill(0).map((_, i) =>
          0.3 + 0.2 * Math.sin((i / 100) * Math.PI * 4)
        );
        setLocalWaveformData(placeholder);
        setIsLoading(false);
        return;
      }

      // Generate real waveform data from the actual audio file (desktop only)
      setIsLoading(true);
      generateWaveformData(audioUrl, 200)
        .then(data => {
          setLocalWaveformData(data);
          setIsLoading(false);
        })
        .catch(error => {
          logger.error('Failed to generate waveform:', error);
          // Fallback to a flat waveform on error
          setLocalWaveformData(Array(100).fill(0.5));
          setIsLoading(false);
        });
    }
  }, [audioUrl, waveformData]);

  // Draw waveform - Record-style envelope rendering
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || localWaveformData.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const canvasHeight = rect.height;

    // Clear canvas with the committed cinematic ground.
    ctx.fillStyle = CANVAS_BACKGROUND;
    ctx.fillRect(0, 0, width, canvasHeight);

    // Draw center line
    const centerY = canvasHeight / 2;
    ctx.strokeStyle = CANVAS_CENTER_LINE;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, centerY);
    ctx.lineTo(width, centerY);
    ctx.stroke();

    const progressPercentage = duration > 0 ? currentTime / duration : 0;
    const progressX = progressPercentage * width;

    // Tempest Record style - amplitude-based color gradient
    const getAmplitudeColor = (amplitude: number, isPlayed: boolean): string => {
      if (!isPlayed) {
        // Unplayed: muted blue tones on the cinematic ground
        if (amplitude > 0.6) return 'rgba(96, 165, 250, 0.4)';
        if (amplitude > 0.3) return 'rgba(59, 130, 246, 0.35)';
        if (amplitude > 0.1) return 'rgba(37, 99, 235, 0.3)';
        return 'rgba(30, 58, 138, 0.25)';
      }
      // Played: vibrant blue gradient based on amplitude
      if (amplitude > 0.95) return '#ef4444'; // Clipping - red
      if (amplitude > 0.85) return '#60a5fa'; // Very loud
      if (amplitude > 0.6) return '#3b82f6';  // Loud
      if (amplitude > 0.3) return '#2563eb';  // Medium
      if (amplitude > 0.1) return '#1d4ed8';  // Quiet
      return '#1e3a8a';                        // Very quiet
    };

    const progressLineColor = '#06b6d4';

    // Record-style: dense pixel columns creating smooth envelope
    // Each pixel column represents multiple data points
    const samplesPerPixel = localWaveformData.length / width;

    // Calculate which sample index corresponds to the progress position
    const progressIdx = Math.floor(progressPercentage * localWaveformData.length);

    // Draw pixel columns for smooth envelope look
    for (let px = 0; px < width; px++) {
      // Get the sample index range for this pixel column
      const startSampleIdx = Math.floor(px * samplesPerPixel);
      const endSampleIdx = Math.min(Math.floor((px + 1) * samplesPerPixel), localWaveformData.length - 1);

      // Find min and max amplitude in this range (creates envelope)
      let minAmp = 1;
      let maxAmp = 0;
      for (let i = startSampleIdx; i <= endSampleIdx; i++) {
        const amp = localWaveformData[i] || 0;
        minAmp = Math.min(minAmp, amp);
        maxAmp = Math.max(maxAmp, amp);
      }

      // Treat single value as symmetric envelope (-amp to +amp)
      const amplitude = maxAmp;

      // Draw the envelope bar (symmetric from center)
      const barHeight = Math.max(1, amplitude * canvasHeight * 0.9);
      const topY = centerY - barHeight / 2;

      // Determine if this pixel is in played or unplayed section
      const isPlayed = startSampleIdx <= progressIdx;
      const color = getAmplitudeColor(amplitude, isPlayed);

      // Add glow effect for very loud played sections
      if (isPlayed && amplitude > 0.85) {
        ctx.shadowColor = '#60a5fa';
        ctx.shadowBlur = 4;
      } else {
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
      }

      ctx.fillStyle = color;
      // Draw 1px wide column (touching adjacent columns for smooth look)
      ctx.fillRect(px, topY, 1, barHeight);
    }

    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    // Draw progress indicator line
    ctx.fillStyle = progressLineColor;
    ctx.fillRect(progressX - 1, 0, 2, canvasHeight);

  }, [localWaveformData, currentTime, duration]);

  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas || duration === 0) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    onSeek(newTime);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  return (
    <div className="waveform-viewer">
      {isLoading && (
        <div className="waveform-loading" style={{ height: `${height}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="spinner" style={{ width: '20px', height: '20px' }}></div>
        </div>
      )}
      <canvas
        ref={canvasRef}
        className={`waveform-canvas ${isHovering ? 'waveform-canvas-hover' : ''}`}
        style={{ width: '100%', height: `${height}px`, display: isLoading ? 'none' : 'block' }}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
    </div>
  );
};

export default WaveformViewer;
